import { ChangeEvent, FormEvent, useState } from 'react';
import { registerUser } from '../api';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { passwordSchema } from '../validation';

const registerSchema = z.object({
  username: z
    .string()
    .min(3, 'Username must be at least 3 characters')
    .max(30, 'Username must be at most 30 characters'),
  email: z.string().email('Invalid email address'),
  password: passwordSchema,
});

type FieldErrors = Partial<
  Record<keyof z.infer<typeof registerSchema>, string>
>;

export default function RegisterPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: '', email: '', password: '' });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = (): boolean => {
    const result = registerSchema.safeParse(form);
    if (!result.success) {
      const errors: FieldErrors = {};
      for (const issue of result.error.issues) {
        const field = issue.path[0] as keyof typeof form;
        if (!errors[field]) {
          errors[field] = issue.message;
        }
      }
      setFieldErrors(errors);
      return false;
    }
    setFieldErrors({});
    return true;
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    if (!validate()) return;

    setLoading(true);
    try {
      const result = await registerUser(
        form.username,
        form.email,
        form.password
      );
      if (result.ok) {
        navigate('/my-games');
      } else {
        setSubmitError(result.error || 'Could not register. Please try again.');
      }
    } catch {
      setSubmitError('Could not register. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="columns">
      <div className="column is-half is-offset-one-quarter">
        <form onSubmit={handleSubmit}>
          <div className="field">
            <label className="label">Username</label>
            <input
              className={`input${fieldErrors.username ? ' is-danger' : ''}`}
              type="text"
              name="username"
              value={form.username}
              onChange={handleChange}
            />
            {fieldErrors.username && (
              <p className="help is-danger">{fieldErrors.username}</p>
            )}
          </div>
          <div className="field">
            <label className="label">Email</label>
            <input
              className={`input${fieldErrors.email ? ' is-danger' : ''}`}
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
            />
            {fieldErrors.email && (
              <p className="help is-danger">{fieldErrors.email}</p>
            )}
          </div>
          <div className="field">
            <label className="label">Password</label>
            <input
              className={`input${fieldErrors.password ? ' is-danger' : ''}`}
              type="password"
              name="password"
              value={form.password}
              onChange={handleChange}
            />
            {fieldErrors.password && (
              <p className="help is-danger">{fieldErrors.password}</p>
            )}
          </div>
          {submitError && (
            <div className="has-text-danger mb-3">{submitError}</div>
          )}
          <button
            className={`button is-primary${loading ? ' is-loading' : ''}`}
            type="submit"
          >
            Register
          </button>
        </form>

        <p className="mt-4">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
